import { useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnEditorOptions } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import moment from 'moment';
import { useToast } from '../../../context/toast/ui';

import { IDialogParams } from '../api/types/dialog.interface';
import tableStyle from '../../assets/table.module.css';
import { Badge } from 'primereact/badge';
import { KdlRow, useMassKdlConsumption } from '../hooks/useMassKdlConsumption';
import { Tooltip } from 'primereact/tooltip';

// eslint-disable-next-line no-unused-vars
export const MassKDLDialog = ({
  show,
  close,
  params: massKDLParams,
}: IDialogParams<any>) => {
  const { showMessage } = useToast();
  const dataTableRef = useRef<DataTable<any>>(null);
  // Функция для закрытия редактирования активной ячейки
  const closeEditingCell = () => {
    dataTableRef.current?.closeEditingCell();
  };

  const {
    rows,
    setRows,
    setMessages,
    getMessageById,
    onCellEditInit,
    onCellEditComplete,
    handleSubmit,
    isPending,
  } = useMassKdlConsumption({
    initialRows: massKDLParams.data.table.ins,
    initialMessages: massKDLParams.data.messages,
    showMessage,
    closeEditingCell,
  });

  useEffect(() => {
    setRows(massKDLParams.data.table.ins);
    setMessages(massKDLParams.data.messages);
  }, [massKDLParams, setRows, setMessages]);

  // Рендер редактора текстового поля
  const textEditor = (options: ColumnEditorOptions) => (
    <InputText
      value={options.value}
      keyfilter="int"
      style={{
        width: 100,
        paddingTop: 8,
        paddingBottom: 7,
        boxShadow: 'none',
      }}
      onChange={(e) => {
        const updatedRows = [...rows];
        updatedRows[options.rowIndex][KdlRow.newConsumption] = e.target.value;
        setRows(updatedRows);
        options.editorCallback!(e.target.value);
      }}
    />
  );

  // Рендер редактора даты
  const dateEditor = (options: ColumnEditorOptions) => (
    <Calendar
      required
      style={{ width: 160 }}
      appendTo="self"
      inputStyle={{ paddingTop: 8, paddingBottom: 7, boxShadow: 'none' }}
      value={options.value}
      onChange={(e) => options.editorCallback!(e.target.value)}
      dateFormat="dd/mm/yy"
    />
  );

  // Рендер редактора времени
  const timeEditor = (options: ColumnEditorOptions) => (
    <Calendar
      required
      style={{ width: 160 }}
      appendTo="self"
      inputStyle={{ paddingTop: 8, paddingBottom: 7, boxShadow: 'none' }}
      value={options.value}
      onChange={(e) => options.editorCallback!(e.target.value)}
      timeOnly
    />
  );

  // Шаблон отображения статуса ячейки
  const statusTemplate = (data: any[]) => {
    const message = getMessageById(+data[KdlRow.counterId]);

    let badgeLabel = 'Данные получены';

    const getBadgeLabelFromType = (type: string) => {
      switch (type) {
        case 'ok':
          return 'Обновлено';
        case 'error':
          return 'Ошибка';
        default:
          return '';
      }
    };

    if (message) {
      badgeLabel = getBadgeLabelFromType(message.message_type);
    } else if (editedStatus(data)) {
      badgeLabel = 'Редактируется';
    }

    const getSeverityByType = (type: string) => {
      switch (type) {
        case 'ok':
          return 'success';
        case 'error':
          return 'danger';
        default:
          return 'info';
      }
    };

    return (
      <>
        <Tooltip target=".tooltip" />
        <Badge
          style={{
            cursor: 'pointer',
          }}
          className="tooltip"
          data-pr-tooltip={message ? message.counter_message : undefined}
          value={badgeLabel}
          severity={message && getSeverityByType(message.message_type)}
        />
      </>
    );
  };

  // Проверка: если по строке имеются изменения
  const editedStatus = (data: any[]) => {
    return rows.find(
      (row) =>
        row[KdlRow.counterId] === data[KdlRow.counterId] &&
        row[KdlRow.newConsumption] !== data[KdlRow.newConsumption]
    );
  };

  return (
    <Dialog
      dismissableMask
      header="Массовая установка показаний счётчиков на КДЛ"
      style={{ width: 1200 }}
      visible={show}
      onHide={close}
    >
      <div
        className={tableStyle.tableContainer}
        style={{
          padding: 0,
        }}
      >
        <DataTable
          showGridlines
          ref={dataTableRef}
          value={rows}
          scrollable
          className={tableStyle.table}
          style={{
            padding: 0,
          }}
          scrollHeight="60dvh"
          virtualScrollerOptions={{ itemSize: 60 }}
          size="small"
          editMode="cell"
        >
          <Column field="1" header="ID"></Column>
          <Column header="Статус" body={statusTemplate}></Column>
          <Column field="0" header="Описание"></Column>
          <Column
            field="7"
            header="Расход"
            style={{ width: 150 }}
            onCellEditInit={onCellEditInit}
            onCellEditComplete={onCellEditComplete}
            onCellEditCancel={onCellEditComplete}
            editor={(options) => textEditor(options)}
          ></Column>
          <Column
            field="8"
            header="Дата снятия показаний"
            onCellEditInit={onCellEditInit}
            onCellEditComplete={onCellEditComplete}
            onCellEditCancel={onCellEditComplete}
            editor={(options) => dateEditor(options)}
            body={(data) => {
              return data[8] && moment(data[8]).format('DD/MM/YYYY');
            }}
          ></Column>
          <Column
            field="9"
            header="Время снятия показаний"
            onCellEditInit={onCellEditInit}
            onCellEditComplete={onCellEditComplete}
            onCellEditCancel={onCellEditComplete}
            editor={(options) => timeEditor(options)}
            body={(data) => data[9] && moment(data[9]).format('HH:mm')}
          ></Column>
        </DataTable>
        <div
          style={{
            display: 'flex',
            justifyContent: 'end',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <Button
            onClick={handleSubmit}
            loading={isPending}
            disabled={isPending}
            label="Установить"
          />
        </div>
      </div>
    </Dialog>
  );
};
